import type {
  AdminLeadPipelineCursor,
  AdminLeadPipelineItem,
  LeadId,
  LeadPipelineStage,
} from "@whitehaven/contracts";
import {
  ContentState,
  FormField,
  MoveControls,
  Select,
  StatusPill,
} from "@whitehaven/ui";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { focusTarget } from "../../focus-management";
import { leadSourceKindLabels } from "./admin-lead-list-model";
import { createAdminLeadDetailHref } from "./lead-detail-route-model";
import { leadAssignableRoleLabels } from "./lead-assignment-model";
import {
  isLeadPipelineStage,
  leadPipelineStageLabels,
  leadPipelineStageOrder,
} from "./pipeline-stage-model";

export type AdminLeadPipelineLaneNotice =
  | "REFRESH_FAILED"
  | "LOAD_MORE_FAILED"
  | null;

export type AdminLeadPipelineLaneViewState =
  | {
      readonly kind: "loading";
      readonly pipelineStage: LeadPipelineStage;
    }
  | {
      readonly kind: "error";
      readonly pipelineStage: LeadPipelineStage;
    }
  | {
      readonly kind: "ready";
      readonly pipelineStage: LeadPipelineStage;
      readonly items: readonly AdminLeadPipelineItem[];
      readonly nextCursor: AdminLeadPipelineCursor | null;
      readonly consumedCursors: readonly AdminLeadPipelineCursor[];
      readonly refreshing: boolean;
      readonly loadingMore: boolean;
      readonly notice: AdminLeadPipelineLaneNotice;
    };

export interface AdminLeadPipelineFeedback {
  readonly tone: "positive" | "critical";
  readonly message: string;
  readonly refreshRequired: boolean;
}

interface AdminLeadPipelineViewProps {
  readonly lanes: readonly AdminLeadPipelineLaneViewState[];
  readonly mutationUnavailableReason: string | null;
  readonly movementDisabledReason: string | null;
  readonly pendingLeadId: LeadId | null;
  readonly feedbackByLeadId: ReadonlyMap<LeadId, AdminLeadPipelineFeedback>;
  readonly announcementLeadId: LeadId | null;
  readonly focusLeadId: LeadId | null;
  readonly onFocusSettled: () => void;
  readonly onRefresh: () => void;
  readonly onRetryLane: (pipelineStage: LeadPipelineStage) => void;
  readonly onRefreshLane: (pipelineStage: LeadPipelineStage) => void;
  readonly onLoadMore: (pipelineStage: LeadPipelineStage) => void;
  readonly onMove: (
    item: AdminLeadPipelineItem,
    targetStage: LeadPipelineStage,
  ) => void;
}

const pipelineDateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Pacific/Honolulu",
});

function formatPipelineDate(value: string): string | null {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? pipelineDateFormatter.format(date)
    : null;
}

function defaultDestination(
  currentStage: LeadPipelineStage,
): LeadPipelineStage {
  const currentIndex = leadPipelineStageOrder.indexOf(currentStage);
  return (
    leadPipelineStageOrder[currentIndex + 1] ??
    leadPipelineStageOrder[currentIndex - 1] ??
    currentStage
  );
}

function PipelineCard({
  item,
  movementDisabledReason,
  pendingLeadId,
  feedback,
  registerFocusTarget,
  onMove,
}: {
  readonly item: AdminLeadPipelineItem;
  readonly movementDisabledReason: string | null;
  readonly pendingLeadId: LeadId | null;
  readonly feedback: AdminLeadPipelineFeedback | undefined;
  readonly registerFocusTarget: (
    leadId: LeadId,
    target: HTMLElement | null,
  ) => void;
  readonly onMove: (
    item: AdminLeadPipelineItem,
    targetStage: LeadPipelineStage,
  ) => void;
}) {
  const generatedId = useId();
  const [targetStage, setTargetStage] = useState<LeadPipelineStage>(() =>
    defaultDestination(item.pipelineStage),
  );
  const pending = pendingLeadId === item.leadId;
  const anotherMovePending =
    pendingLeadId !== null && pendingLeadId !== item.leadId;
  const requiresRefresh = feedback?.refreshRequired === true;
  const movementDisabled =
    movementDisabledReason !== null || anotherMovePending;
  const disabledReason = movementDisabledReason
    ? movementDisabledReason
    : anotherMovePending
      ? "Another lead stage change is being confirmed."
      : undefined;
  const detailHref = createAdminLeadDetailHref(item.leadId);
  const formattedUpdatedAt = formatPipelineDate(item.updatedAt);
  const possibleMatches =
    item.duplicateSummary.eligibility === "TARGET_INACTIVE"
      ? "Not checked for an inactive record"
      : item.duplicateSummary.candidateContactCount === 0
        ? "No exact normalized-channel matches"
        : `${item.duplicateSummary.candidateContactCount} possible via ${item.duplicateSummary.matchKinds
            .map((kind) => (kind === "EMAIL" ? "email" : "phone"))
            .join(" + ")}`;

  useEffect(() => {
    setTargetStage(defaultDestination(item.pipelineStage));
  }, [item.pipelineStage]);

  return (
    <article
      className="crm-pipeline-card"
      tabIndex={-1}
      ref={(target) => registerFocusTarget(item.leadId, target)}
    >
      <div className="crm-pipeline-card__heading">
        <div>
          <p className="crm-pipeline-card__eyebrow">Authorized lead</p>
          <h3>
            {detailHref ? (
              <a href={detailHref}>{item.displayName}</a>
            ) : (
              item.displayName
            )}
          </h3>
        </div>
      </div>

      <dl className="crm-pipeline-card__facts">
        <div>
          <dt>Last updated</dt>
          <dd>
            {formattedUpdatedAt ? (
              <time dateTime={item.updatedAt}>{formattedUpdatedAt}</time>
            ) : (
              "Unavailable"
            )}
          </dd>
        </div>
        <div>
          <dt>Source</dt>
          <dd>
            {item.sourceKind
              ? `${leadSourceKindLabels[item.sourceKind]}${
                  item.sourceKey ? ` · ${item.sourceKey}` : ""
                }`
              : "Not captured"}
          </dd>
        </div>
        <div>
          <dt>Assigned to</dt>
          <dd>
            {item.assignedUserId === null
              ? "Unassigned"
              : item.assignedTo
                ? `${item.assignedTo.displayLabel} · ${
                    leadAssignableRoleLabels[item.assignedTo.role]
                  }`
                : "Unavailable assignee"}
          </dd>
        </div>
        <div>
          <dt>Possible matches</dt>
          <dd>
            {item.duplicateSummary.candidateContactCount > 0 ? (
              <StatusPill tone="warning">{possibleMatches}</StatusPill>
            ) : (
              possibleMatches
            )}
          </dd>
        </div>
      </dl>

      <MoveControls
        id={`${generatedId}-move`}
        className="crm-pipeline-card__move"
        itemLabel="lead"
        legend="Change pipeline stage"
        instructions="Choose any different fixed stage, then confirm the change."
        currentPosition={leadPipelineStageLabels[item.pipelineStage]}
        currentPositionLabel="Current stage"
        actions={[
          {
            id: "move",
            label: "Move lead",
            disabled: requiresRefresh,
            ...(requiresRefresh && feedback
              ? { disabledReason: feedback.message }
              : {}),
          },
        ]}
        busy={pending}
        busyLabel="Changing the pipeline stage."
        disabled={movementDisabled}
        disabledReason={disabledReason}
        live={false}
        {...(feedback
          ? { status: feedback.message, statusTone: feedback.tone }
          : {})}
        onMoveRequest={() => onMove(item, targetStage)}
      >
        <FormField id={`${generatedId}-destination`} label="Destination stage">
          {(controlProps) => (
            <Select
              {...controlProps}
              value={targetStage}
              disabled={pending || movementDisabled || requiresRefresh}
              onChange={(event) => {
                const destination = event.currentTarget.value;
                if (
                  isLeadPipelineStage(destination) &&
                  destination !== item.pipelineStage
                ) {
                  setTargetStage(destination);
                }
              }}
            >
              {leadPipelineStageOrder
                .filter((stage) => stage !== item.pipelineStage)
                .map((stage) => (
                  <option key={stage} value={stage}>
                    {leadPipelineStageLabels[stage]}
                  </option>
                ))}
            </Select>
          )}
        </FormField>
      </MoveControls>
    </article>
  );
}

function LaneState({ children }: { readonly children: ReactNode }) {
  return <div className="crm-pipeline-lane__state">{children}</div>;
}

export function AdminLeadPipelineView({
  lanes,
  mutationUnavailableReason,
  movementDisabledReason,
  pendingLeadId,
  feedbackByLeadId,
  announcementLeadId,
  focusLeadId,
  onFocusSettled,
  onRefresh,
  onRetryLane,
  onRefreshLane,
  onLoadMore,
  onMove,
}: AdminLeadPipelineViewProps) {
  const cardFocusTargets = useRef(new Map<LeadId, HTMLElement>());
  const allReady = lanes.every((lane) => lane.kind === "ready");
  const loadedCount = lanes.reduce(
    (count, lane) => count + (lane.kind === "ready" ? lane.items.length : 0),
    0,
  );
  const boardBusy =
    pendingLeadId !== null ||
    lanes.some(
      (lane) =>
        lane.kind === "loading" ||
        (lane.kind === "ready" && (lane.refreshing || lane.loadingMore)),
    );
  const initialLoading = lanes.some((lane) => lane.kind === "loading");
  const attentionLaneCount = lanes.filter(
    (lane) =>
      lane.kind === "error" || (lane.kind === "ready" && lane.notice !== null),
  ).length;
  const announcedFeedback = announcementLeadId
    ? feedbackByLeadId.get(announcementLeadId)
    : undefined;
  const boardStatus =
    pendingLeadId !== null
      ? "Changing one pipeline stage. The card remains in its last confirmed lane."
      : announcedFeedback
        ? announcedFeedback.message
        : initialLoading
          ? "Loading the authorized pipeline."
          : attentionLaneCount > 0
            ? `${attentionLaneCount} ${
                attentionLaneCount === 1 ? "stage needs" : "stages need"
              } attention. Last confirmed cards remain where available.`
            : boardBusy
              ? "Updating the confirmed pipeline view."
              : `${loadedCount} loaded ${
                  loadedCount === 1 ? "lead" : "leads"
                } across ten fixed stages.`;

  useEffect(() => {
    if (!focusLeadId) return;

    const frame = window.requestAnimationFrame(() => {
      focusTarget(cardFocusTargets.current.get(focusLeadId) ?? null);
      onFocusSettled();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusLeadId, onFocusSettled]);

  return (
    <div className="crm-pipeline" aria-busy={boardBusy ? "true" : undefined}>
      {mutationUnavailableReason && (
        <ContentState
          className="crm-pipeline__notice"
          kind="warning"
          headingLevel={2}
          title="The pipeline is read-only."
          live={false}
        >
          <p>Confirmed cards remain available. {mutationUnavailableReason}</p>
        </ContentState>
      )}

      <div className="crm-pipeline__toolbar">
        <div>
          <p
            className="crm-pipeline__summary"
            role={announcedFeedback?.tone === "critical" ? "alert" : "status"}
            aria-live={
              announcedFeedback?.tone === "critical" ? "assertive" : "polite"
            }
            aria-atomic="true"
          >
            {boardStatus}
          </p>
          <p>
            Counts describe only loaded cards. Stage labels are internal
            workflow classifications.
          </p>
        </div>
        <button
          type="button"
          className="crm-pipeline__button"
          aria-disabled={boardBusy ? "true" : undefined}
          onClick={() => {
            if (!boardBusy) onRefresh();
          }}
        >
          Refresh pipeline
        </button>
      </div>

      <nav className="crm-pipeline__jumps" aria-label="Pipeline stages">
        <span>Jump to stage</span>
        <ul>
          {leadPipelineStageOrder.map((stage) => (
            <li key={stage}>
              <a href={`#crm-pipeline-stage-${stage.toLowerCase()}`}>
                {leadPipelineStageLabels[stage]}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {allReady && loadedCount === 0 && (
        <ContentState
          kind="empty"
          headingLevel={2}
          title="No active leads are available in the pipeline."
          live={false}
        >
          <p>
            The authorized bounded reads completed without returning any active
            lead cards. Empty stages remain visible below.
          </p>
        </ContentState>
      )}

      <section
        className="crm-pipeline__board"
        aria-label="CRM lead pipeline"
        tabIndex={0}
      >
        {lanes.map((lane, laneIndex) => {
          const stage = lane.pipelineStage;
          const headingId = `crm-pipeline-stage-${stage.toLowerCase()}`;

          return (
            <section
              className="crm-pipeline-lane"
              aria-labelledby={headingId}
              key={stage}
            >
              <header className="crm-pipeline-lane__header">
                <div>
                  <p>
                    Stage {laneIndex + 1} of {leadPipelineStageOrder.length}
                  </p>
                  <h2 id={headingId}>{leadPipelineStageLabels[stage]}</h2>
                </div>
                <StatusPill>
                  {lane.kind === "ready"
                    ? `${lane.items.length} loaded`
                    : "Pending"}
                </StatusPill>
              </header>

              {lane.kind === "loading" && (
                <LaneState>
                  <ContentState
                    kind="loading"
                    headingLevel={3}
                    title={`Loading ${leadPipelineStageLabels[stage]} leads.`}
                    live={false}
                  />
                </LaneState>
              )}

              {lane.kind === "error" && (
                <LaneState>
                  <ContentState
                    kind="error"
                    headingLevel={3}
                    title="This stage could not be loaded."
                    live={false}
                    actions={
                      <button
                        type="button"
                        className="crm-pipeline__button"
                        aria-disabled={
                          pendingLeadId !== null ? "true" : undefined
                        }
                        onClick={() => {
                          if (pendingLeadId === null) onRetryLane(stage);
                        }}
                      >
                        Try stage again
                      </button>
                    }
                  >
                    <p>No unconfirmed cards are shown.</p>
                  </ContentState>
                </LaneState>
              )}

              {lane.kind === "ready" && (
                <>
                  {lane.notice && (
                    <ContentState
                      className="crm-pipeline-lane__notice"
                      kind="error"
                      headingLevel={3}
                      live={false}
                      title={
                        lane.notice === "REFRESH_FAILED"
                          ? "This stage could not be refreshed."
                          : "More cards could not be loaded."
                      }
                      actions={
                        <button
                          type="button"
                          className="crm-pipeline__button"
                          aria-disabled={
                            pendingLeadId !== null ? "true" : undefined
                          }
                          onClick={() => {
                            if (pendingLeadId !== null) return;
                            if (lane.notice === "REFRESH_FAILED") {
                              onRefreshLane(stage);
                            } else {
                              onLoadMore(stage);
                            }
                          }}
                        >
                          Try again
                        </button>
                      }
                    >
                      <p>The last confirmed cards remain visible.</p>
                    </ContentState>
                  )}

                  {lane.items.length === 0 ? (
                    <p className="crm-pipeline-lane__empty">
                      No loaded active leads in this stage.
                    </p>
                  ) : (
                    <ol className="crm-pipeline-lane__cards">
                      {lane.items.map((item) => (
                        <li key={item.leadId}>
                          <PipelineCard
                            item={item}
                            movementDisabledReason={movementDisabledReason}
                            pendingLeadId={pendingLeadId}
                            feedback={feedbackByLeadId.get(item.leadId)}
                            registerFocusTarget={(leadId, target) => {
                              if (target) {
                                cardFocusTargets.current.set(leadId, target);
                              } else {
                                cardFocusTargets.current.delete(leadId);
                              }
                            }}
                            onMove={onMove}
                          />
                        </li>
                      ))}
                    </ol>
                  )}

                  {lane.nextCursor && (
                    <button
                      type="button"
                      className="crm-pipeline__button crm-pipeline__button--secondary"
                      aria-disabled={
                        lane.loadingMore ||
                        lane.refreshing ||
                        pendingLeadId !== null
                          ? "true"
                          : undefined
                      }
                      onClick={() => {
                        if (
                          !lane.loadingMore &&
                          !lane.refreshing &&
                          pendingLeadId === null
                        ) {
                          onLoadMore(stage);
                        }
                      }}
                    >
                      {lane.loadingMore ? "Loading cards" : "Load more cards"}
                    </button>
                  )}
                </>
              )}
            </section>
          );
        })}
      </section>
    </div>
  );
}
